import { tokens } from "lib/colors"
import ReactSelect, {
  components,
  GroupBase,
  mergeStyles,
  OptionProps,
  PropsValue,
  SelectInstance,
  StylesConfig,
  ValueContainerProps,
  Props as ReactSelectProps,
} from "react-select"
import CreatableSelect from "react-select/creatable"
import FormField from "lib/components/Form/FormField"
import { resolveFieldStatus } from "lib/components/Form/fieldStyles"
import {
  buildSelectStyles,
  customCssLayer,
  resolveSelectBorder,
  selectChip,
} from "lib/components/Form/selectStyles"
import SelectChevron from "lib/components/Form/SelectChevron"
import {
  isItemDisabled,
  resolveSelectBehaviour,
  SelectVariant,
} from "lib/components/Form/selectBehaviour"
import {
  Children,
  CSSProperties,
  forwardRef,
  ReactNode,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react"

export type SelectItem = {
  value: string | number
  label: string
  /** Disables the option under either spelling; `isDisabled` is react-select's own field. */
  disabled?: boolean
  isDisabled?: boolean
}

export type SelectValue = PropsValue<SelectItem>
/** Typed as the single-select instance so hosts holding a plain `SelectInstance` keep compiling; it is the same object either way. */
export type SelectRef = SelectInstance<SelectItem, false, GroupBase<SelectItem>>
type InternalRef = SelectInstance<SelectItem, boolean, GroupBase<SelectItem>>
type Styles = StylesConfig<SelectItem, boolean, GroupBase<SelectItem>>

/** Forwards component-level flags to react-select so custom parts can read them from `selectProps`. */
interface ExtraSelectProps extends ReactSelectProps<SelectItem, boolean, GroupBase<SelectItem>> {
  customSingleValuePrefix?: string
  /** Total selectable options; the select-all row compares this to `selectedCount`. */
  selectableCount?: number
  selectedCount?: number
}

export interface SelectProps {
  name?: string
  label?: string
  labelTooltips?: ReactNode
  placeholder?: ReactNode
  success?: boolean
  error?: boolean
  message?: string
  /** Shows a neutral hint under the box; `message` replaces it while `error` is set. */
  helperText?: ReactNode
  disabled?: boolean
  noBorder?: boolean
  onlyBorderBottom?: boolean
  /** Hides the indicator separator when the host draws its own divider beside the control. */
  hideSeparator?: boolean
  /** Applies to the control after every design-system rule, so it always wins. */
  customCss?: CSSProperties

  /** Renders one chip per selected value, wrapping as needed; defaults to true for the checkbox variant. */
  isMulti?: boolean
  /** `checkbox` renders checkbox option rows, a one-chip `+N` summary, select-all and `N Selected`. */
  variant?: SelectVariant
  /** Lets the user add the typed text as a new option. */
  creatable?: boolean

  options: SelectItem[]
  value?: SelectValue
  /** Receives what react-select emits: one item, a readonly array, or null. Kept as `any` because the host handlers read `next.value` inline; a union would break them all. */
  onChange: (next: any) => void
  handleInputChange?: ReactSelectProps<SelectItem, boolean>["onInputChange"]
  onFocus?: ReactSelectProps<SelectItem, boolean>["onFocus"]
  onBlur?: ReactSelectProps<SelectItem, boolean>["onBlur"]
  autoFocus?: boolean
  isLoading?: boolean
  isSearchable?: boolean
  /** Overrides the variant default; left unset, react-select derives it from `isMulti`. */
  isClearable?: boolean
  noOptionsMessage?: string
  /** Stops further picks once this many are selected (multi only). */
  maxLimit?: number
  /** Controlled menu state; left unset, react-select manages the menu itself. */
  menuIsOpen?: boolean
  onMenuOpen?: () => void
  onMenuClose?: () => void

  hasSelectAll?: boolean
  /** Prefixes the summary chip with muted text (checkbox variant). */
  customSingleValuePrefix?: string

  validateCreateOption?: (inputValue: string) => boolean
  validationMessage?: string
  setError?: (name: string, error: { type: string; message: string }) => void
}

const SELECT_ALL_VALUE = "select-all"

function toArray(value: SelectValue | undefined): SelectItem[] {
  if (Array.isArray(value)) return [...(value as readonly SelectItem[])]
  return value ? [value as SelectItem] : []
}

/** Legacy group heading look, carried over unchanged from the old fork. */
const groupHeadingLayer: Styles = {
  groupHeading: (styles) => ({
    ...styles,
    color: tokens.neutral[70],
    fontSize: "0.975rem",
    textTransform: "capitalize",
  }),
}

/** Keeps the `+N` summary on one row (the chip truncates instead of wrapping) and rotates the chevron. */
const checkboxLayer: Styles = {
  // `minWidth: 0` is what lets the chip shrink instead of pushing the input onto a second row.
  valueContainer: (styles) => ({ ...styles, flexWrap: "nowrap", minWidth: 0, overflow: "hidden" }),
  dropdownIndicator: (styles, { selectProps }) => ({
    ...styles,
    transition: "transform 0.2s ease",
    transform: selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
  }),
}

type ItemOptionProps = OptionProps<SelectItem, boolean, GroupBase<SelectItem>> & {
  selectProps: ExtraSelectProps
}

const CheckboxOption = (props: ItemOptionProps) => {
  const { data, isSelected, isDisabled, selectProps } = props

  // The "Add …" row is an action rather than a value, so it keeps the plain rendering.
  // eslint-disable-next-line no-underscore-dangle -- react-select's own flag on the create row
  if ((data as SelectItem & { __isNew__?: boolean }).__isNew__) {
    return <components.Option {...props} />
  }

  const isSelectAll = data.value === SELECT_ALL_VALUE
  const allSelected = isSelectAll && selectProps.selectedCount === selectProps.selectableCount

  return (
    <components.Option {...props}>
      {/* Decorative: the option row owns the click, a label would dispatch it twice. */}
      <span className="flex items-center gap-2 w-full">
        <input
          type="checkbox"
          checked={isSelectAll ? allSelected : isSelected}
          className="form-checkbox"
          disabled={isDisabled}
          tabIndex={-1}
          aria-hidden
          readOnly
        />
        {data.label}
      </span>
    </components.Option>
  )
}

/** Renders the first selection plus a `+N` chip instead of one chip per value. */
const SummaryValueContainer = (
  props: ValueContainerProps<SelectItem, boolean, GroupBase<SelectItem>> & {
    selectProps: ExtraSelectProps
  },
) => {
  const {
    getValue,
    isDisabled,
    children,
    selectProps: { customSingleValuePrefix, instanceId, menuIsOpen },
  } = props

  const selectedValues = getValue()
  const selectedCount = selectedValues.length
  const prefix = customSingleValuePrefix || ""
  const childrenArray = Children.toArray(children)
  const inputChild = childrenArray[childrenArray.length - 1] ?? null
  const restChildren = childrenArray.slice(0, -1)
  const showSummary = selectedValues.length > 0
  const chip = selectChip({ disabled: isDisabled })

  useEffect(() => {
    if (!instanceId) return
    const sanitizedInstanceId = instanceId.toString().replace(/([^\w-])/g, "\\$1")
    const input = document.querySelector<HTMLInputElement>(
      `.custom-dropdown-select-all-${sanitizedInstanceId} input`,
    )
    if (input && menuIsOpen) {
      setTimeout(() => input.focus(), 0)
    }
    // `getValue()` returns a fresh array each render; keying on its length keeps the refocus to
    // real selection changes. Mirrors share-lib, which still keys on the array (upstream candidate).
  }, [instanceId, menuIsOpen, selectedCount])

  return (
    <components.ValueContainer
      {...props}
      className={`flex flex-row gap-x-1.5 items-center custom-dropdown-select-all-${instanceId}`}>
      {showSummary ? (
        <>
          {prefix && <span className="shrink-0 text-xs text-neutral-50">{prefix}</span>}
          <div className={`${chip} min-w-0 max-w-[65%]`}>{selectedValues[0].label}</div>
          {selectedValues.length > 1 && (
            <div className={`${chip} shrink-0`}>+{selectedValues.length - 1}</div>
          )}
          {/* Stays mounted: react-select drives the menu from this input. */}
          <div className="flex-1 min-w-[1px]">{inputChild}</div>
          <div className="hidden">{restChildren}</div>
        </>
      ) : (
        children
      )}
    </components.ValueContainer>
  )
}

/**
 * Renders the design-system react-select field in its single, multi, checkbox-summary and creatable shapes.
 * `variant="checkbox"` mirrors the selection so select-all works even when the field is uncontrolled.
 */
const Select = forwardRef<SelectRef, SelectProps>(
  (
    {
      name,
      label,
      labelTooltips,
      placeholder = "Select...",
      success,
      error,
      message,
      helperText,
      disabled = false,
      noBorder = false,
      onlyBorderBottom = false,
      hideSeparator = false,
      customCss,
      isMulti: isMultiProp,
      variant = "default",
      creatable = false,
      options,
      value,
      onChange,
      handleInputChange,
      onFocus,
      onBlur,
      autoFocus = false,
      isLoading = false,
      isSearchable = true,
      isClearable,
      noOptionsMessage = "No options",
      maxLimit,
      menuIsOpen,
      onMenuOpen,
      onMenuClose,
      hasSelectAll = false,
      customSingleValuePrefix,
      validateCreateOption,
      validationMessage = "Invalid input",
      setError,
    },
    ref,
  ) => {
    const controlId = useId()
    // Without an explicit instanceId react-select falls back to a module-level counter that
    // keeps incrementing across SSR requests, so its generated ids never match the client's.
    // `name` alone is not enough — several call-sites omit it and /orders renders two selects
    // named "type" — so every derived id is namespaced per instance.
    const uid = useId().replace(/:/g, "")
    const instanceId = name ? `${name}-${uid}` : uid
    const internalRef = useRef<InternalRef>(null)
    const [bodyElement, setBodyElement] = useState<HTMLElement | null>(null)
    const isCheckbox = variant === "checkbox"
    const isMulti = isMultiProp ?? isCheckbox
    // Controlled fields read the selection from `value`; the mirror only serves uncontrolled ones.
    const [uncontrolledSelection, setUncontrolledSelection] = useState<SelectItem[]>(() =>
      toArray(value),
    )
    const selectedOptions = value !== undefined ? toArray(value) : uncontrolledSelection
    const status = resolveFieldStatus(error, success)
    const showMessage = status === "danger" && Boolean(message)
    const valueLength = toArray(value).length
    const behaviour = resolveSelectBehaviour({ variant, isMulti, valueLength, placeholder })

    const tokenLayer = buildSelectStyles<SelectItem, boolean>({
      status,
      border: resolveSelectBorder(noBorder, onlyBorderBottom),
      hideSeparator,
    })
    const componentLayer = isCheckbox
      ? mergeStyles(groupHeadingLayer, checkboxLayer)
      : groupHeadingLayer
    const styles = mergeStyles(
      mergeStyles(tokenLayer, componentLayer),
      customCssLayer<SelectItem, boolean>(customCss),
    )

    const items = options ?? []
    const selectableOptions = items.filter((item) => !isItemDisabled(item, 0))
    const selectAllOption: SelectItem = { label: "Select All", value: SELECT_ALL_VALUE }
    const menuOptions = isCheckbox && hasSelectAll ? [selectAllOption, ...items] : items

    const extraSelectProps: Partial<ExtraSelectProps> = {
      ...(customSingleValuePrefix ? { customSingleValuePrefix } : {}),
      ...(isCheckbox
        ? { selectableCount: selectableOptions.length, selectedCount: selectedOptions.length }
        : {}),
    }

    useImperativeHandle(ref, () => internalRef.current as SelectRef)

    useEffect(() => {
      setBodyElement(document.body)
    }, [])

    const handleChange = (next: SelectValue) => {
      if (!isCheckbox) {
        onChange(next)
        return
      }
      const selected = toArray(next)
      const pickedSelectAll =
        hasSelectAll && selected.some((item) => item.value === SELECT_ALL_VALUE)
      let resolved = selected
      if (pickedSelectAll) {
        resolved = selectedOptions.length === selectableOptions.length ? [] : selectableOptions
      }
      setUncontrolledSelection(resolved)
      onChange(resolved)
    }

    const handleCreateOption = (inputValue: string) => {
      if (validateCreateOption && !validateCreateOption(inputValue)) {
        if (setError && name) setError(name, { type: "manual", message: validationMessage })
        return
      }
      const newOption: SelectItem = { value: inputValue, label: inputValue }
      // Appends to the current selection, not to `options`.
      const next = isMulti ? [...toArray(value), newOption] : newOption
      if (isCheckbox) setUncontrolledSelection(toArray(next))
      onChange(next)
    }

    const selectComponents = {
      DropdownIndicator: SelectChevron,
      ...(isCheckbox ? { Option: CheckboxOption, ValueContainer: SummaryValueContainer } : {}),
    }

    const commonProps: ExtraSelectProps = {
      classNamePrefix: "react-select",
      menuPortalTarget: bodyElement,
      instanceId,
      inputId: controlId,
      "aria-invalid": status === "danger" || undefined,
      "aria-errormessage": showMessage ? `${controlId}-helper` : undefined,
      "aria-label": name,
      name,
      options: menuOptions,
      value,
      onChange: handleChange,
      onInputChange: handleInputChange,
      onFocus,
      onBlur,
      autoFocus,
      isMulti,
      isDisabled: disabled,
      isSearchable,
      isLoading,
      isClearable: isClearable ?? behaviour.isClearable,
      closeMenuOnSelect: behaviour.closeMenuOnSelect,
      hideSelectedOptions: behaviour.hideSelectedOptions,
      blurInputOnSelect: behaviour.blurInputOnSelect,
      maxMenuHeight: behaviour.maxMenuHeight,
      placeholder: behaviour.placeholder,
      noOptionsMessage: () => noOptionsMessage,
      isOptionDisabled: (item, selected) =>
        isItemDisabled(
          item,
          valueLength,
          maxLimit,
          selected.some((s) => s.value === item.value),
        ),
      menuIsOpen,
      onMenuOpen,
      onMenuClose,
      styles,
      components: selectComponents,
      ...extraSelectProps,
    }

    return (
      <FormField
        controlId={controlId}
        label={label}
        labelTooltips={labelTooltips}
        name={name}
        status={status}
        disabled={disabled}
        helperText={helperText}
        message={message}
        className="w-full">
        <div className="relative flex-1">
          {creatable ? (
            <CreatableSelect
              ref={internalRef}
              {...commonProps}
              onCreateOption={handleCreateOption}
              formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
            />
          ) : (
            <ReactSelect ref={internalRef} {...commonProps} />
          )}
        </div>
      </FormField>
    )
  },
)

Select.displayName = "Select"

export default Select
